import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import HelpCenterPdfScrollViewer from "./HelpCenterPdfScrollViewer";
import Close from "../../components/Atomos/Icons/Close";
import { normalizeDocumentUrl } from "../../utils/normalizeDocumentUrl";

type HelpCenterPdfViewerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
  title?: string;
};

export default function HelpCenterPdfViewerModal({
  isOpen,
  onClose,
  pdfUrl,
  title = "PDF Instructions",
}: HelpCenterPdfViewerModalProps) {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen || !pdfUrl?.trim()) {
      setPdfBlob(null);
      setError("");
      return;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError("");

      try {
        const normalizedUrl = normalizeDocumentUrl(pdfUrl);
        const response = await fetch(normalizedUrl, {
          credentials: "include",
          mode: "cors",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch PDF (${response.status})`);
        }

        const blob = await response.blob();
        const pdfBlobResult = blob.type.includes("pdf")
          ? blob
          : new Blob([await blob.arrayBuffer()], { type: "application/pdf" });

        if (!cancelled) {
          setPdfBlob(pdfBlobResult);
        }
      } catch {
        if (!cancelled) {
          setPdfBlob(null);
          setError("Unable to load the PDF instructions.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pdfUrl]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="help-center-pdf-viewer-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="help-center-pdf-viewer-modal"
        onClick={(event) => event.stopPropagation()}
        onContextMenu={(event) => event.preventDefault()}
      >
        <div className="help-center-pdf-viewer-header">
          <div className="help-center-pdf-viewer-title-wrap">
            <span className="help-center-pdf-viewer-badge" aria-hidden="true">
              PDF
            </span>
            <h2 className="help-center-pdf-viewer-title">{title}</h2>
          </div>
          <button
            type="button"
            className="help-center-pdf-viewer-close"
            onClick={onClose}
            aria-label="Close PDF viewer"
          >
            <Close className="help-center-pdf-viewer-close-icon" />
          </button>
        </div>

        <div className="help-center-pdf-viewer-body">
          {error ? (
            <div className="help-center-pdf-viewer-error">{error}</div>
          ) : (
            <HelpCenterPdfScrollViewer
              pdfBlob={pdfBlob}
              loading={loading}
              loadingMessage="Loading PDF instructions..."
              errorMessage="Unable to render PDF instructions."
              emptyMessage="No PDF instructions available."
            />
          )}
        </div>
      </div>
      <p className="help-center-pdf-viewer-dismiss-hint">
        Press Esc or click outside to close
      </p>
    </div>,
    document.body,
  );
}
